import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const Badge = ({ ok, label, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold ${
      ok ? "bg-emerald-50 text-emerald-700 border border-emerald-300" : "bg-red-50 text-red-700 border border-red-300"
    }`}
  >
    <span className={`w-2 h-2 rounded-full ${ok ? "bg-emerald-500" : "bg-red-500 animate-pulse"}`} />
    {label}
  </span>
);

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "—");

function HubSpotRow({ hs }) {
  const ok = hs.configured && hs.up;
  return (
    <div className="py-4 border-b border-[var(--gold)]/20" data-testid="integration-hubspot-row">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm uppercase tracking-[0.18em] font-semibold text-[var(--emerald)]">HubSpot checkout</div>
        <Badge ok={ok} label={!hs.configured ? "Off" : ok ? "Working" : "Down"} testId="hubspot-checkout-badge" />
      </div>
      <p className="text-xs text-[var(--ink-2)] mt-2 leading-relaxed" data-testid="hubspot-checkout-detail">
        {!hs.configured
          ? "Reservations use Stripe (HubSpot checkout is switched off)."
          : ok
          ? "Guests are sent to your HubSpot payment link."
          : `${hs.error || "HubSpot is erroring"} — guests are paying by card on Stripe until it's fixed.`}{" "}
        Last checked {fmtTime(hs.checked_at)}.
      </p>
    </div>
  );
}

function TwilioRow({ tw, onTest, testing }) {
  const [phone, setPhone] = useState("");
  return (
    <div className="py-4" data-testid="integration-twilio-row">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm uppercase tracking-[0.18em] font-semibold text-[var(--emerald)]">Twilio texts</div>
        <Badge ok={tw.ok} label={tw.ok ? "Connected" : tw.configured ? "Error" : "Not set up"} testId="twilio-badge" />
      </div>
      <p className="text-xs text-[var(--ink-2)] mt-2 leading-relaxed" data-testid="twilio-detail">
        {tw.ok
          ? `Sending from ${tw.messaging_service_name ? `"${tw.messaging_service_name}"` : tw.from_number}.`
          : `${tw.error}.`}{" "}
        {tw.pending_texts} text{tw.pending_texts === 1 ? "" : "s"} waiting in queue.
      </p>
      {tw.ok && (
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Your mobile, e.g. +1 555 123 4567"
            className="flex-1 border border-[var(--gold)]/50 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[var(--emerald)]"
            data-testid="twilio-test-phone"
          />
          <button
            type="button"
            disabled={!phone.trim() || testing}
            onClick={() => onTest(phone)}
            className="inline-flex items-center justify-center gap-2 bg-[var(--emerald)] text-[var(--ivory)] px-4 py-2 text-xs uppercase tracking-[0.18em] disabled:opacity-40 hover:bg-[var(--brand-red)] transition-colors"
            data-testid="twilio-test-send"
          >
            <Send size={12} strokeWidth={1.5} /> {testing ? "Sending…" : "Send test text"}
          </button>
        </div>
      )}
    </div>
  );
}

export const IntegrationStatusCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/admin/integrations/health${refresh ? "?refresh=true" : ""}`);
      setData(d);
    } catch {
      toast.error("Couldn't load integration status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(), 60000);
    return () => clearInterval(t);
  }, [load]);

  const sendTest = async (phone) => {
    setTesting(true);
    try {
      const { data: d } = await api.post("/admin/twilio/test-sms", { phone });
      toast.success(`Test text sent to ${d.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test text failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="mt-8 border border-[var(--gold)]/40 bg-white px-6 py-2" data-testid="integration-status-card">
      <div className="flex items-center justify-between pt-4">
        <div className="overline">Live status</div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={loading}
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--gold)] hover:text-[var(--brand-red)] transition-colors"
          data-testid="integration-recheck-btn"
        >
          <RefreshCw size={12} strokeWidth={1.5} className={loading ? "animate-spin" : ""} /> Recheck now
        </button>
      </div>
      {!data ? (
        <div className="py-6 text-xs text-[var(--ink-2)]">Checking…</div>
      ) : (
        <>
          <HubSpotRow hs={data.hubspot_checkout} />
          <TwilioRow tw={data.twilio} onTest={sendTest} testing={testing} />
        </>
      )}
    </div>
  );
};

export default IntegrationStatusCard;
